// ============================================
// components/BlocklyViewer.tsx
// Blockly 工作区只读渲染 (C-积木预览, 2026-05-20 接通).
//
// 实现:
//   1. 动态 import('blockly') — 600KB 大库,懒加载,不拖首屏
//   2. 注册积木块定义:
//      - 内置块: defineArduinoBlocks() (复制自 Studio blocks.ts, 127 个块)
//      - 云端动态块: registerCloudBlocks() (复用 Studio 同一个 manifest)
//   3. Blockly.inject 只读模式渲染 workspace_xml
//   4. 渲染后量内容尺寸,自适应高度
// ============================================

import { useEffect, useRef, useState } from 'react';
import { Puzzle } from 'lucide-react';

interface Props {
  xml: string | null;
  boardType?: string;
  height?: number | string;
  collapsible?: boolean;
}

let blocksRegistered = false;

// 容错: 给 XML 里出现、但当前预览端尚未注册的积木类型, 临时注册一个灰色占位块,
//   避免 Blockly.Xml.domToWorkspace 因"Invalid block definition"整个抛错导致预览全挂。
//   这是版本不同步时的安全网; 根治靠 manifest 同步(见 cloudBlocks.ts), 这里是防御层。
//   以后 Studio 再加新积木而 manifest 一时没跟上, 预览也只会显示占位块, 不会整页失败。
function ensureUnknownBlocksDefined(Blockly: any, dom: Element) {
  let nodes: Element[] = [];
  try { nodes = Array.from(dom.querySelectorAll('block, shadow')); } catch { return; }
  const seen = new Set<string>();
  for (const el of nodes) {
    const type = el.getAttribute('type');
    if (!type || seen.has(type)) continue;
    seen.add(type);
    if (Blockly.Blocks[type]) continue;   // 已有定义(内置 blocks.ts 或 manifest)
    const inValue = !!(el.closest && el.closest('value'));   // 在 <value> 内 = 值块(需 output 接口)
    const def: any = {
      type,
      message0: '未知积木: %1',
      args0: [{ type: 'field_label', text: type }],
      colour: '#9aa0a6',
      tooltip: '该积木在预览端未定义(积木版本不同步)',
      inputsInline: true,
    };
    if (inValue) def.output = null;
    else { def.previousStatement = null; def.nextStatement = null; }
    try { Blockly.defineBlocksWithJsonArray([def]); } catch { /* ignore */ }
  }
}

export default function BlocklyViewer({ xml, boardType, collapsible }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<any>(null);
  const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'ready' | 'error' | 'empty'>('idle');
  const [collapsed, setCollapsed] = useState(false);
  const [contentHeight, setContentHeight] = useState(460);

  useEffect(() => {
    if (!xml || !xml.trim()) {
      setLoadStatus('empty');
      return;
    }
    if (collapsed) return;

    let disposed = false;
    setLoadStatus('loading');

    (async () => {
      try {
        const Blockly = await import('blockly');
        const { defineArduinoBlocks, defineIoTBlocks } = await import('../blockly/blocks');
        const { registerCloudBlocks } = await import('../blockly/cloudBlocks');

        if (!blocksRegistered) {
          try { defineArduinoBlocks(); } catch (e) { console.warn('[BlocklyViewer] 内置块注册:', e); }
          try { defineIoTBlocks(); } catch (e) { console.warn('[BlocklyViewer] IoT块注册:', e); }
          // 官方核心积木 = 上面的 defineArduinoBlocks/defineIoTBlocks(blocks.ts),
          //   该文件由 sync-blocks.js 从 Studio 逐字同步, 即权威源, 不再单独拉 official.json。
          try { await registerCloudBlocks(); } catch (e) { console.warn('[BlocklyViewer] 云端块注册:', e); }
          blocksRegistered = true;
        }

        if (disposed || !containerRef.current) return;

        if (workspaceRef.current) {
          try { workspaceRef.current.dispose(); } catch { /* ignore */ }
          workspaceRef.current = null;
        }

        const ws = Blockly.inject(containerRef.current, {
          readOnly: true,
          trashcan: false,
          toolbox: undefined,
          scrollbars: true,
          move: { scrollbars: true, drag: true, wheel: false },
          zoom: { controls: true, wheel: false, startScale: 0.8, maxScale: 1.5, minScale: 0.4 },
          // zelos 渲染器, 与 Studio 一致(圆润造型, Scratch 风格)
          renderer: 'zelos',
          // 与 Studio 一致的主题: base=Classic(块带描边质感),
          // 而非默认 Zelos 主题(扁平无描边)
          theme: (Blockly as any).Theme.defineTheme('hipiido_light', {
            name: 'hipiido_light',
            base: (Blockly as any).Themes.Classic,
            componentStyles: {
              workspaceBackgroundColour: '#f9fafb',
              scrollbarColour: '#c7c7cc',
              scrollbarOpacity: 0.6,
            },
          }),
        });
        workspaceRef.current = ws;

        const dom = Blockly.utils.xml.textToDom(xml);
        ensureUnknownBlocksDefined(Blockly, dom);   // 容错: 未注册的积木先补占位块, 防止整页渲染崩溃
        Blockly.Xml.domToWorkspace(dom, ws);

        try {
          const metrics = ws.getMetricsManager?.().getContentMetrics?.();
          if (metrics && metrics.height) {
            const h = Math.min(760, Math.max(320, Math.ceil(metrics.height * 0.8) + 120));
            setContentHeight(h);
          }
        } catch { /* default height */ }

        setTimeout(() => {
          if (disposed) return;
          try {
            (Blockly as any).svgResize(ws);
            ws.scrollCenter?.();
          } catch { /* ignore */ }
        }, 60);

        if (!disposed) setLoadStatus('ready');
      } catch (e) {
        console.error('[BlocklyViewer] 渲染失败:', e);
        if (!disposed) setLoadStatus('error');
      }
    })();

    return () => {
      disposed = true;
      if (workspaceRef.current) {
        try { workspaceRef.current.dispose(); } catch { /* ignore */ }
        workspaceRef.current = null;
      }
    };
  }, [xml, collapsed]);

  if (loadStatus === 'empty') {
    return (
      <div className="bg-paper border border-ink-300/40 rounded-xl p-8 text-center">
        <div className="flex justify-center mb-2"><Puzzle className="w-7 h-7 text-ink-300" strokeWidth={1.5} /></div>
        <p className="text-sm text-ink-400">这个作品没有积木工作区(纯代码模式)</p>
      </div>
    );
  }

  if (loadStatus === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-sm text-red-700">
        积木预览加载失败,请刷新重试
      </div>
    );
  }

  return (
    <div>
      {collapsible && (
        <button
          className="text-sm text-gold-600 hover:underline mb-2"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? '展开积木工作区' : '收起积木工作区'}
        </button>
      )}
      <div className="relative">
        <div
          ref={containerRef}
          className="bg-[#f9fafb] border border-ink-300/40 rounded-lg overflow-hidden"
          style={{
            height: `${contentHeight}px`,
            display: collapsed ? 'none' : 'block',
          }}
        />
        {loadStatus === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center text-ink-400 text-sm bg-[#f9fafb]/80 rounded-lg">
            正在加载积木预览…
          </div>
        )}
        {loadStatus === 'ready' && boardType && (
          <span className="absolute top-2 right-2 text-[11px] text-ink-400 bg-white/80 px-2 py-0.5 rounded-pill border border-ink-300/40">
            {boardType}
          </span>
        )}
      </div>
    </div>
  );
}
